import PropTypes from "prop-types";
import React, { useEffect } from "react";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { Tabs, Tab } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import {
  fetchCourses,
  fetchCourseSession
} from "../../../../redux/actions/contentCoursesActions";
import {
  buildCourseDetailsSessionsRoute,
  buildCourseSessionDetailsRoute,
  buildCourseSessionHomeworkRoute,
  buildCourseSessionAttendanceRoute,
  buildCourseSessionEnrollEmployeesRoute,
  courseSession,
  courseSessionDetails,
  courseSessionHomework,
  courseSessionAttendance,
  courseSessionEnrollEmployees
} from "../../../../routes/AdminRoutes";
import { Route, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CourseSessionDetails from "./CourseSessionDetails";
import CourseSessionAttendance from "./CourseSessionAttendance";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import CourseSessionEnrollEmployees from "./CourseSessionEnrollEmployees";
import { isSessionClosedOut } from "../../../../utils/course_utils";
import Homework from "../Homework/Homework";

const selector = makeGetEntities();

const getCourseSessionRoutes = (courseId, sessionId) => {
  return [
    {
      route: buildCourseSessionDetailsRoute(courseId, sessionId)
    },
    {
      route: buildCourseSessionHomeworkRoute(courseId, sessionId)
    },
    {
      route: buildCourseSessionEnrollEmployeesRoute(courseId, sessionId)
    },
    {
      route: buildCourseSessionAttendanceRoute(courseId, sessionId)
    }
  ];
};

const CourseSession = ({
  location: { pathname },
  match: {
    params: { courseId, sessionId }
  }
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = React.useState(0);

  const { courseData, sessionData, loading } = useSelector(
    ({ contentCoursesState }) => ({
      courseData: selector(contentCoursesState, {
        type: "trainingObject",
        id: courseId
      }),
      sessionData: selector(contentCoursesState, {
        type: "trainingObject",
        id: sessionId
      }),
      loading: contentCoursesState.loading
    })
  );

  useEffect(() => {
    dispatch(fetchCourses(courseId, { include: "userAssignments" }));
  }, []);

  function handleTabChange(newTab) {
    dispatch(push(getCourseSessionRoutes(courseId, sessionId)[newTab].route));
    setCurrentTab(newTab);
  }

  const handleGoBackToCourseSessionsView = () => {
    dispatch(push(buildCourseDetailsSessionsRoute(courseId)));
  };

  useEffect(() => {
    const currentPathIndex = getCourseSessionRoutes(
      courseId,
      sessionId
    ).findIndex(tab => tab.route === pathname);
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    }
    dispatch(fetchCourseSession(sessionId));
  }, [pathname]);

  const sessionName = sessionData ? sessionData.name : "";
  const courseName = courseData ? courseData.name : "";

  const disableEnrollTab = sessionData
    ? isSessionClosedOut(sessionData.statusTypeId)
    : true;

  return (
    <AdminPageLayout
      title={<Breadcrumb routes={["Courses", courseName, sessionName]} />}
      onGoBackClick={handleGoBackToCourseSessionsView}
    >
      <Tabs
        value={currentTab}
        onChange={(e, newTab) => handleTabChange(newTab)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons={false}
      >
        <Tab label={t("courseSession.detailsTab")} />
        <Tab label={t("courseSession.homeworkTab")} />
        <Tab
          label={t("courseSession.enrollEmployeesTab")}
          disabled={disableEnrollTab}
        />
        <Tab label={t("courseSession.attendanceTab")} />
      </Tabs>
      {sessionData && courseData ? (
        <>
          <Route
            exact
            path={courseSession}
            render={() => (
              <Redirect
                to={buildCourseSessionDetailsRoute(courseId, sessionId)}
              />
            )}
          />
          <Route
            exact
            path={courseSessionHomework}
            render={props => (
              <Homework
                {...props}
                training_object_id={sessionId}
                courseType="ilt"
              />
            )}
          />
          <Route
            exact
            path={courseSessionDetails}
            render={props => (
              <CourseSessionDetails {...props} sessionData={sessionData} />
            )}
          />
          <Route
            exact
            path={courseSessionEnrollEmployees}
            render={props => (
              <CourseSessionEnrollEmployees
                {...props}
                sessionData={sessionData}
                courseData={courseData}
              />
            )}
          />
          <Route
            exact
            path={courseSessionAttendance}
            render={props => (
              <CourseSessionAttendance
                courseId={courseId}
                sessionData={sessionData}
              />
            )}
          />
        </>
      ) : loading ? (
        <LoadingSpinner />
      ) : // TODO: check for error & show error view
      null}
    </AdminPageLayout>
  );
};

CourseSession.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default CourseSession;
